import type { AIMetaType, MetaType, NoliboxMetaModelType, PromptInfo } from "@/types/meta";
import type { Txt2ImgSizes } from "@/stores/status";
import { Dictionary, Lang, range } from "@noli/core";
import { NoliboxModelTypes } from "@/modules/editor/constant";

type ENV_TYPE = "test" | "uat" | "production";
export const ENV: ENV_TYPE = process.env.REACT_APP_ENV as ENV_TYPE;

// misc

export const CONTAINER_ID = "board.container";

export const ID_CACHE_KEY = "noliId";
export const NAME_CACHE_KEY = "noliName";
export const GRAPH_CACHE_KEY = "noliGraph";
export const USER_ID_CACHE_KEY = "noliUserId";
export const TRANSFORM_CACHE_KEY = "noliTransform";
export const UPDATE_TIME_CACHE_KEY = "noliUpdateTime";
export const COMMIT_ID_CACHE_KEY = "noliCommitId";
export const PREVIEW_CACHE_KEY = "noliPreview";

export const LEFT_WIDTHS = ["384px"];
export const LEFT_PANEL_WIDTH = 384;
export const RIGHT_WIDTHS = ["320px"];
export const RIGHT_PANEL_WIDTH = 320;

export const AI_MAX_WH = 1024;
export const LANDSCAPE_MAX_WH = 768;
export const INPAINTING_MAX_WH = 1280;
export const INPAINTING_ALGORITHM_MAX_WH = 512;
export const SUPER_RESOLUTION_MAX_WH = 1024;
export const SUPER_RESOLUTION_POST_MAX_WH = 768;
export const HIGH_RES_ADVANCE_MODE_MAX_WH = 832;

export const TASK_LIMIT = 4;
export const MAX_PENDING = 20;
export const ACCELERATE_FACTOR = 3;
export const ACCELERATE_PATIENCE = 10;

export const SD_VERSION = "v1.5";
export const DREAMLIKE_VERSION = "dreamlike_v1";

export const VERSION_MODEL_MAP = {
  [SD_VERSION]: "basic",
  anime: "anime",
  [DREAMLIKE_VERSION]: "art",
};

export const NOLI_MODEL_META_MODAL_MAP: Record<NoliboxModelTypes, NoliboxMetaModelType> = {
  nolibox: "basic",
  anime: "anime",
  dreamlike: "art",
  "deliberate-general": "general",
  "epicrealisim-real": "real",
  "revanime-3d-anime": "3danime",
};

export const META_MODAL_NOLI_MODEL_MAP: Record<NoliboxMetaModelType, NoliboxModelTypes> = {
  basic: "nolibox",
  anime: "anime",
  art: "dreamlike",
  general: "deliberate-general",
  real: "epicrealisim-real",
  "3danime": "revanime-3d-anime",
};

export const ONE_DAY_MILLISECONDS = 1000 * 60 * 60 * 24;

// external

export const imageLoadingGIFs: Record<Lang, string> = {
  zh: "https://ailabcdn.nolibox.com/gif/static/creator_loading_20221009_0957.gif",
  en: "https://ailabcdn.nolibox.com/gif/static/creator_loading_20221009_0957.gif",
};

// txt2img

export const allTxt2ImgStyles = [
  "none-style",
  "anime-style",
  "epic-style",
  "cg-style",
  "oil-painting-style",
  "water-color-style",
  "photograph-style",
  "cyberpunk-style",
  "futurism-style",
  "sketch-style",
  "cartoon-style",
  "chinese-painting-style",
  "neon-game-style",
  "ghibli-style",
  "crystal-punk-style",
  "psychedelic-style",
  "low-poly-style",
  "pixel-style",
  "matte-painting-style",
  "interior-design-style",
  "vaporwave-style",
  "impressionism-style",
  "ukiyo-e-style",
  "retrowave-palette-style",
  "city-pop-style",
  "chinese-new-style",
] as const;
export const allTxt2ImgArtists = [
  "none-artist",
  "ghibli-artist",
  "mucha-artist",
  "van-gogh-artist",
  "daVinci-artist",
  "vermeer-artist",
  "ingres-artist",
  "monet-artist",
  "rembrandt-artist",
  "fengzikai-artist",
  "wuguanzhong-artist",
  "zhangdaqian-artist",
  "durer-artist",
  "picasso-artist",
  "cezanne-artist",
  "matisse-artist",
  "dali-artist",
  "mondrian-artist",
] as const;
export type Txt2ImgStyles = typeof allTxt2ImgStyles[number];
export type Txt2ImgArtists = typeof allTxt2ImgArtists[number];
export const styleMapping: Record<Txt2ImgStyles, string[]> = {
  "none-style": ["8k", "4k", "high definition", "HD", "highly detailed", "wallpaper"],
  "oil-painting-style": [
    "oil painting",
    "a beautiful impasto oil painting",
    "oil painting with nice colour scheme",
  ],
  "water-color-style": ["watercolor painting", "beautiful detailed watercolor", "watercolor with"],
  "sketch-style": [
    "sketch",
    "sketch, smooth lines",
    "pen and ink",
    "pencil sketch",
    "pencil drawing",
  ],
  "cartoon-style": [
    "2D, cartoon style, Disney's Animated, animation pixar style,  by pendleton ward, magali villeneuve, artgerm, rob rey and kentaro miura style",
  ],
  "chinese-painting-style": [
    "Chinese style painting",
    "Chinese painting",
    "vivid ink painting with chinese style",
    "traditional Chinese painting",
    "Chinese watercolor",
    "Chinese court painting",
  ],
  "photograph-style": [
    "photography",
    "photorealistic, photography",
    "detailed sharp photo",
    "photograph with sharp focus",
    "photograph by canon",
  ],
  "anime-style": ["animation style", "an anime drawing", "manga style with vivid color"],
  "neon-game-style": [
    "glowing neon pink blocks floating around, by peter mohrbacher dan mumford craig mullins nekro, cgsociety, pixiv, volumetric light, 3d render",
  ],
  "cg-style": ["CG scenario"],
  "ghibli-style": ["Ghibli style", "Artwork by studio ghibli"],
  "crystal-punk-style": ["opalescent crystal, magical, hyperrealistic, octane render"],
  "psychedelic-style": ["vivid colors, detailed lines, detailed portrait, poster style"],
  "low-poly-style": [
    "gradient low poly art with connecting lines",
    "low-poly",
    "low ploygon",
    "low poly",
    "isometric low poly",
    "lowpoly",
    "low-poly with volumetric light",
  ],
  "pixel-style": ["pixel art", "pixel"],
  "matte-painting-style": ["highly detailed matte painting", "matte painting"],
  "interior-design-style": [
    "octane render, amazing colors, bloom, hdri, 8k high quality, trending on art station",
  ],
  "cyberpunk-style": ["Cyberpunk style"],
  "vaporwave-style": ["Vaporwave style"],
  "impressionism-style": ["Impressionism style"],
  "futurism-style": ["futurism style"],
  "epic-style": ["epic composition"],
  "ukiyo-e-style": ["Ukiyo-e style", "Ukiyo-e art"],
  "retrowave-palette-style": [
    "retrowave palette, digital world, highly detailed, electric breeze, anatomically correct, synth feel,  4k digital art",
  ],
  "city-pop-style": ["80s city pop style"],
  "chinese-new-style": ["by huang guangjian, fenghua zhong, ruan jia, xin jin and wei chang."],
};
export const artistMapping: Record<Txt2ImgArtists, string[]> = {
  "none-artist": [""],
  "daVinci-artist": ["by Leonardo DaVinci", "by Leonardo da Vinci"],
  "van-gogh-artist": ["by Vincent Van Gogh"],
  "vermeer-artist": ["by Johannes Vermeer"],
  "rembrandt-artist": ["by Rembrandt"],
  "durer-artist": ["by Albrecht Dürer"],
  "picasso-artist": ["by Pablo Picasso", "by Pablo Ruiz Picasso"],
  "cezanne-artist": ["by Paul Cézanne"],
  "monet-artist": ["by Claude Monet"],
  "ingres-artist": ["by Jean-Auguste-Dominique Ingres"],
  "mucha-artist": ["by Mucha"],
  "ghibli-artist": ["by studio ghibli"],
  "matisse-artist": ["by Matisse"],
  "dali-artist": ["by Dalí"],
  "mondrian-artist": ["by Mondrian"],
  "fengzikai-artist": ["by Feng Zikai"],
  "wuguanzhong-artist": ["by Wu Guanzhong"],
  "zhangdaqian-artist": ["by Zhang Daqian"],
};

export const STYLE_IMAGES: Dictionary<any> = {};
export const ARTIST_IMAGES: Dictionary<any> = {};
allTxt2ImgStyles.forEach(
  (style) =>
    (STYLE_IMAGES[style] = require(`../assets/images/styles/${style.slice(
      0,
      style.length - "-style".length,
    )}.png`)),
);
allTxt2ImgArtists.forEach(
  (artist) =>
    (ARTIST_IMAGES[artist] = require(`../assets/images/artists/${artist.slice(
      0,
      artist.length - "-artist".length,
    )}.png`)),
);

export type Recommendation = {
  word: string;
  name: string;
  en_name: string;
  data: {
    size: Txt2ImgSizes;
    use_circular: boolean;
    is_anime: boolean;
    promptInfo: PromptInfo;
    enPromptInfo: PromptInfo;
    model?: NoliboxMetaModelType;
    num_steps?: number;
    guidance_scale?: number;
  };
};
export const recommendations: readonly Recommendation[] = [
  {
    word: "recommend1",
    name: "国风少女",
    en_name: "Pretty Anime Chinese Girl",
    data: {
      size: "3:4",
      use_circular: false,
      is_anime: true,
      promptInfo: {
        lang: "zh",
        original:
          "{{{masterpiece}}},精致五官,masterpiece,完美五官,精致细节,眼睛,头发氛围,高分辨率,4K画质,最佳光线,细腻肌肤,细节光线,大师作品,best quality,the highest quality ,杰作,(大师杰作),极致细节,(精致五官),(精致头发刻画),(精致眼睛刻画),精致手绘,4k画质,绚丽光影,气泡水滴,破碎感, 精致发型，柔软凌乱的头发，精致的辫子包子，精致的珍珠贝壳发饰，红唇，神韵，仙气，水雾，超细节，精致旗袍，精致薄纱，半身，立绘，花朵，合一，（超- 详细CG：1.2) , (8K: 1.2), 室内, , {{{ {{wind blowing}}}}} , glowing light, {{{{{{{half body}} }}}}}}, { {{{{光粒子}}}}}，非常详细",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "{{{masterpiece}}},exquisite facial features,masterpiece,perfect facial features,exquisite details,eyes,hair atmosphere,high resolution,4K quality,best light,delicate skin,detailed light,master work, best quality, The highest quality, masterpiece, (masterpiece of a master), extreme detail, (delicate facial features), (delicate hair depiction), (delicate eyes depiction), delicate hand depiction, 4k painting quality, gorgeous light and shadow, bubble drops, broken feeling, delicate hairstyle, soft and messy hair, delicate braid buns, delicate pearl shell hair ornaments, red lips, divinity, immortality, water mist, super detail, exquisite cheongsam, Exquisite tulle, half body, vertical painting, flowers，Unity, (super-detailed CG: 1.2) , (8K: 1.2), interior, , {{{ {{wind blowing}}}}} , glowing light, {{{{{{{half body}} }}}}}}, {{{{{light particle}}}}}, highly detailed",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend2",
    name: "精灵少女",
    en_name: "Elvish maiden",
    data: {
      size: "3:4",
      use_circular: false,
      is_anime: true,
      promptInfo: {
        lang: "zh",
        original:
          "{{{masterpiece}}},精致五官,masterpiece,完美五官,精致细节,眼睛,头发氛围,高分辨率,4K画质,最佳光线,细腻肌肤,细节光线,杰作,best quality,higher quality,high 细节，美女，景深，潮湿，发光的黄色叶子衣服，七彩彩虹发光明亮的卷发女孩，脸部微距拍摄，光泽的嘴唇，眼影，人像，人像特写，蝴蝶，玫瑰，花式头饰，高领毛衣，精灵耳朵， 触手，Unity，（超精细CG：1.2），（8K：1.2），室内，{{{ {{wind blowing}}} }}，发光灯，{{{{{{{半身}}}}} }}}},{{{{{光粒子}}}}}},高清",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "{{{masterpiece}}},exquisite facial features,masterpiece,perfect facial features,exquisite details,eyes,hair atmosphere,high resolution,4K quality,best light,delicate skin,detailed light,masterwork ,best quality,higher quality,high detail,beauty,depth of field,wet,glowing yellow leaf clothes,colorful rainbow glowing bright curly hair girl,face macro shot,glossy lips,eye shadow,portrait,portrait closeup, Butterflies, roses, fancy tiaras, turtlenecks, elf ears, tentacles, Unity, (super detailed CG: 1.2), (8K: 1.2), indoors, {{{ {{wind blowing}}} }}, glowing lights , {{{{{{{half body}}}}}}}}},{{{{{light particle}}}}}}, high definition",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend3",
    name: "光明和森林女神",
    en_name: "Light and Forest Goddess",
    data: {
      size: "3:4",
      use_circular: false,
      is_anime: true,
      promptInfo: {
        lang: "zh",
        original:
          "{{{masterpiece}}},精致五官,masterpiece,完美五官,精致细节,眼睛,头发氛围,高分辨率,4K画质,最佳光线,细腻肌肤,细节光线,杰作,best quality,higher quality,high 细节 人头鹿身 人马座 美丽的女脸 纤长的睫毛 银色的长发 几根辫子 水晶弓箭 水晶半透明的鹿角 花冠 银白色的毛皮包裹全身 无肉 水晶森林 魔法 圈圈，杰作，杰作，超高清，无数金色光点，梦幻神韵，人马拟人，七彩光影，顶光，逼真效果，等级安全，统一，（超精细CG：1.2），（8K：1.2） , 室内, {{{ {{wind blowing}}} }}, 发光的光, {{{{{{{半身}}}}}}}}}, {{{{ {light particle}}}}}} , 4k",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "{{{masterpiece}}},exquisite facial features,masterpiece,perfect facial features,exquisite details,eyes,hair atmosphere,high resolution,4K quality,best light,delicate skin,detailed light,masterwork ,best quality,higher quality, high detail, human head and deer body Sagittarius, beautiful female face, slender eyelashes, silver long hair, several braids, crystal bows and arrows, crystal translucent antlers, flower crown, silver-white fur wrapping the whole body, Meatless, Crystal Forest, Magic Circle, Masterpiece, Masterpiece, Ultra HD, Countless Golden Light Spots, Dreamy and Divine, Sagittarius Anthropomorphic, Colorful Light and Shadow, Top Lighting, Realistic Effects, Rating Safety, Unity, (Super Fine CG: 1.2), (8K: 1.2), indoor, {{{ {{wind blowing}}} }}, glowing light, {{{{{{{half body}}}}}}}}}, {{{{ {light particle}}}}}, 4k",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend4",
    name: "多彩河边小屋",
    en_name: "Gorgeous river house",
    data: {
      model: "art",
      num_steps: 30,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original:
          "河边的美丽小屋，艺术家Victo Ngai, Kilian Eng和Jake Parker，充满活力的色彩线条，获奖杰作，华丽丰富，octane渲染，8K高清分辨率",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "stunning cottage on a river, artstation winner by Victo Ngai, Kilian Eng and by Jake Parker, swirly vibrant color lines, winning-award masterpiece, fantastically gaudy, aesthetic octane render, 8K HD Resolution, 8k",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend5",
    name: "猫咪钢笔画",
    en_name: "Ink drawings of cats",
    data: {
      model: "basic",
      num_steps: 25,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original:
          "非常详细的猫肖像，华丽，摄影现实主义背景，艺术家James Gilleard, Joe Fenton， Kaethe Butcher，在instagram上流行，获奖细节，8k",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "highly detailed portrait of cat, magnificent, photographic realistic background, by james gilleard, by joe fenton, by kaethe butcher, trending on instagram, award winning details , 8k",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend6",
    name: "面包机工业设计",
    en_name: "Breadmine Industry Design",
    data: {
      model: "art",
      num_steps: 30,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original: "Dieter Rams设计的烤面包机，复杂的渲染。 电影产品拍摄",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "out of frame, duplicate, watermark, signature, text, nsfw, bad anatomy, missing anatomy, fused anatomy, extra anatomy, malformed , deformed, mutilated, morbid,",
      },
      enPromptInfo: {
        lang: "en",
        original: "Toaster designed by Dieter Rams， Intricate render. Cinematic product shot",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "out of frame, duplicate, watermark, signature, text, nsfw, bad anatomy, missing anatomy, fused anatomy, extra anatomy, malformed , deformed, mutilated, morbid,",
      },
    },
  },
  {
    word: "recommend7",
    name: "简约白色客厅",
    en_name: "Simple white living room",
    data: {
      model: "art",
      num_steps: 30,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original:
          "带有家具的极简白色空间大厅，摄影高细节，极简主义，超现实主义，高细节，体积照明，全局照明，8k，哑光绘画，电影高度详细的超风格化细节，4k",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "out of frame, duplicate, watermark, signature, text, nsfw, bad anatomy, missing anatomy, fused anatomy, extra anatomy, malformed , deformed, mutilated, morbid,",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "minimal white space lobby with forniture, photography high details, minimalism, hyperrealism, high detail, volumetric lighting, global illumination, 8k, matte painting, cinematic highly detailed hyperstylized details, HD",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "out of frame, duplicate, watermark, signature, text, nsfw, bad anatomy, missing anatomy, fused anatomy, extra anatomy, malformed , deformed, mutilated, morbid,",
      },
    },
  },
  {
    word: "recommend8",
    name: "赛博朋克城市",
    en_name: "Cyberpunk city street",
    data: {
      model: "art",
      num_steps: 30,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original:
          "超细节的美丽的赛博朋克城市街道，高分辨率，高清晰度，艺术家Ilya Kuvshinov, Greg Rutkowski和Makoto Shinkai",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "A ultra detailed beautiful painting of a cyberpunk city street, oil panting, high resolution 4K, by Ilya Kuvshinov, Greg Rutkowski and Makoto   Shinkai, high definition",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend9",
    name: "梵高田园风景",
    en_name: "Van Gogh's rural landscape",
    data: {
      model: "basic",
      num_steps: 25,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original: "一幅有着麦田、村庄、风车、麦垛的风景油画",
        style: "oil-painting-style",
        artist: "van-gogh-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original: "Wheat fields, villages, windmills, wheat stacks",
        style: "oil-painting-style",
        artist: "van-gogh-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend10",
    name: "秋日风景",
    en_name: "Autumn Landscape",
    data: {
      model: "art",
      num_steps: 30,
      guidance_scale: 14,
      size: "1:1",
      use_circular: false,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original:
          "地上满是树叶的森林，高大的秋天树木，让小条纹的光线照射到地面，由 Goro Fujita 绘画，清晰的焦点，高度详细，艺术站",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original:
          "a forest full of leaves on the ground, tall autumn trees that let in small streaks of light to the ground, painting by goro fujita, sharp focus, highly detailed, artstation ",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend11",
    name: "传统花卉纹样",
    en_name: "Traditional floral pattern",
    data: {
      model: "art",
      num_steps: 25,
      guidance_scale: 14,
      size: "1:1",
      use_circular: true,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original: "传统纹样，黄牡丹，蓝色背景",
        style: "water-color-style",
        artist: "ghibli-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original: "Traditional pattern, yellow peonies, blue background",
        style: "water-color-style",
        artist: "ghibli-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
  {
    word: "recommend12",
    name: "花鸟纹样",
    en_name: "Bird and flower pattern",
    data: {
      model: "art",
      num_steps: 30,
      guidance_scale: 14,
      size: "1:1",
      use_circular: true,
      is_anime: false,
      promptInfo: {
        lang: "zh",
        original: "色彩缤纷，淡黑色纸上的鸟和花，非常详细的插图，草图，概念艺术，墨水轮廓，平滑",
        style: "water-color-style",
        artist: "ghibli-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
      enPromptInfo: {
        lang: "en",
        original: "Traditional pattern, yellow peonies, blue background",
        style: "none-style",
        artist: "none-artist",
        originalNegative:
          "cropped, blurred, mutated, error, lowres, blurry, low quality, username, signature, watermark, text, nsfw, missing limb, fused hand, missing hand, extra limbs, malformed limbs, bad hands, extra fingers, fused fingers, missing fingers, bad breasts, deformed, mutilated, morbid, bad anatomy, malformed, disfigured, ugly, extra ears, bad ears, fused ears, liquid ears, bad hairs, fused hairs, cross-eyed, extra eyes, bad eyes, fused eyes, unclear eyes, bad face, fused face, big face, long face",
      },
    },
  },
] as const;
export type RecommendationWords = typeof recommendations[number]["word"];
export const recommendationDictionary: Record<Lang, Dictionary<string>> = {
  zh: {},
  en: {},
};
recommendations.forEach(({ word, name, en_name }) => {
  recommendationDictionary.zh[word] = name;
  recommendationDictionary.en[word] = en_name;
});

// txt2img - presets

export const allTxt2ImgCats = [
  "none-cat",
  "yingduan-cat",
  "jiafei-cat",
  "buou-cat",
  "jumao-cat",
  "bosi-cat",
  "meiduan-cat",
  "xianluo-cat",
  "xinjiapo-cat",
  "ximalaya-cat",
  "sikeke-cat",
  "zhonghuatianyuan-cat",
  "mianyin-cat",
  "wumaomao-cat",
] as const;
export type Txt2ImgCats = typeof allTxt2ImgCats[number];
export const catMapping: Record<Txt2ImgCats, string[]> = {
  "none-cat": ["cat"],
  "yingduan-cat": ["British shorthair cat"],
  "jiafei-cat": ["Exotic shorthair cat"],
  "buou-cat": ["Ragdoll cat"],
  "bosi-cat": ["Persian cat"],
  "xianluo-cat": ["Siamese cat"],
  "meiduan-cat": ["American Shorthair cat"],
  "jumao-cat": ["a golden cat type of (Chinese Felis silvestris catus)"],
  "xinjiapo-cat": ["a cat type of ((Singapurar))"],
  "ximalaya-cat": ["a cat type of Colourpoint Persians, Himalaya Longhair cat"],
  "sikeke-cat": ["Selkirk Rex"],
  "zhonghuatianyuan-cat": ["Felis catus, Chinese Felis silvestris catus"],
  "mianyin-cat": ["a cat type of MaineCoon"],
  "wumaomao-cat": ["Sphynx cat"],
};

// semantic stuffs

export const semanticMap: { [Key: number]: string } = {
  0: "unlabeled",
  1: "person",
  2: "bicycle",
  3: "car",
  4: "motorcycle",
  5: "airplane",
  6: "bus",
  7: "train",
  8: "truck",
  9: "boat",
  10: "traffic light",
  11: "fire hydrant",
  12: "street sign",
  13: "stop sign",
  14: "parking meter",
  15: "bench",
  16: "bird",
  17: "cat",
  18: "dog",
  19: "horse",
  20: "sheep",
  21: "cow",
  22: "elephant",
  23: "bear",
  24: "zebra",
  25: "giraffe",
  26: "hat",
  27: "backpack",
  28: "umbrella",
  29: "shoe",
  30: "eye glasses",
  31: "handbag",
  32: "tie",
  33: "suitcase",
  34: "frisbee",
  35: "skis",
  36: "snowboard",
  37: "sports ball",
  38: "kite",
  39: "baseball bat",
  40: "baseball glove",
  41: "skateboard",
  42: "surfboard",
  43: "tennis racket",
  44: "bottle",
  45: "plate",
  46: "wine glass",
  47: "cup",
  48: "fork",
  49: "knife",
  50: "spoon",
  51: "bowl",
  52: "banana",
  53: "apple",
  54: "sandwich",
  55: "orange",
  56: "broccoli",
  57: "carrot",
  58: "hot dog",
  59: "pizza",
  60: "donut",
  61: "cake",
  62: "chair",
  63: "couch",
  64: "potted plant",
  65: "bed",
  66: "mirror",
  67: "dining table",
  68: "window",
  69: "desk",
  70: "toilet",
  71: "door",
  72: "tv",
  73: "laptop",
  74: "mouse",
  75: "remote",
  76: "keyboard",
  77: "cell phone",
  78: "microwave",
  79: "oven",
  80: "toaster",
  81: "sink",
  82: "refrigerator",
  83: "blender",
  84: "book",
  85: "clock",
  86: "vase",
  87: "scissors",
  88: "teddy bear",
  89: "hair drier",
  90: "toothbrush",
  91: "hair brush",
  92: "banner",
  93: "blanket",
  94: "branch",
  95: "bridge",
  96: "building-other",
  97: "bush",
  98: "cabinet",
  99: "cage",
  100: "cardboard",
  101: "carpet",
  102: "ceiling-other",
  103: "ceiling-tile",
  104: "cloth",
  105: "clothes",
  106: "clouds",
  107: "counter",
  108: "cupboard",
  109: "curtain",
  110: "desk-stuff",
  111: "dirt",
  112: "door-stuff",
  113: "fence",
  114: "floor-marble",
  115: "floor-other",
  116: "floor-stone",
  117: "floor-tile",
  118: "floor-wood",
  119: "flower",
  120: "fog",
  121: "food-other",
  122: "fruit",
  123: "furniture-other",
  124: "grass",
  125: "gravel",
  126: "ground-other",
  127: "hill",
  128: "house",
  129: "leaves",
  130: "light",
  131: "mat",
  132: "metal",
  133: "mirror-stuff",
  134: "moss",
  135: "mountain",
  136: "mud",
  137: "napkin",
  138: "net",
  139: "paper",
  140: "pavement",
  141: "pillow",
  142: "plant-other",
  143: "plastic",
  144: "platform",
  145: "playingfield",
  146: "railing",
  147: "railroad",
  148: "river",
  149: "road",
  150: "rock",
  151: "roof",
  152: "rug",
  153: "salad",
  154: "sand",
  155: "sea",
  156: "shelf",
  157: "sky-other",
  158: "skyscraper",
  159: "snow",
  160: "solid-other",
  161: "stairs",
  162: "stone",
  163: "straw",
  164: "structural-other",
  165: "table",
  166: "tent",
  167: "textile-other",
  168: "towel",
  169: "tree",
  170: "vegetable",
  171: "wall-brick",
  172: "wall-concrete",
  173: "wall-other",
  174: "wall-panel",
  175: "wall-stone",
  176: "wall-tile",
  177: "wall-wood",
  178: "water-other",
  179: "waterdrops",
  180: "window-blind",
  181: "window-other",
  182: "wood",
};
export const reversedSemanticMap: Dictionary<number> = {};
Object.entries(semanticMap).forEach(([k, v]) => (reversedSemanticMap[v] = parseInt(k)));

type SemanticColor = [number, number, number];
function getAllSemanticColors(): Dictionary<SemanticColor> {
  function uint82bin(n: number, count = 8): string {
    return range(0, count)
      .reverse()
      .map((x) => ((n >> x) & 1).toString())
      .join("");
  }

  const range_ = range(0, Object.keys(semanticMap).length);
  const colors: SemanticColor[] = range_.map(() => [0, 0, 0]);
  range_.forEach((i) => {
    let r = 0;
    let g = 0;
    let b = 0;
    let id = i + 1;
    range(0, 7).forEach((j) => {
      const strId = uint82bin(id);
      r = r ^ (parseInt(strId[strId.length - 1]) << (7 - j));
      g = g ^ (parseInt(strId[strId.length - 2]) << (7 - j));
      b = b ^ (parseInt(strId[strId.length - 3]) << (7 - j));
      id = id >> 3;
    });
    colors[i][0] = r;
    colors[i][1] = g;
    colors[i][2] = b;
  });

  range_.forEach((i) => {
    const importantColor = importantColors[semanticMap[i]];
    if (importantColor) {
      colors[i] = importantColor;
    }
  });

  const mapping: Dictionary<SemanticColor> = {};
  colors.forEach((color, i) => (mapping[semanticMap[i]] = color));
  return mapping;
}
function colorToString(color: SemanticColor): string {
  const [r, g, b] = color;
  return `rgb(${r},${g},${b})`;
}

export const importantColors: Dictionary<SemanticColor> = {
  clouds: [170, 170, 170],
  grass: [29, 195, 49],
  hill: [200, 146, 117],
  mountain: [116, 77, 52],
  river: [157, 157, 255],
  // rock: [237, 78, 31],
  sea: [54, 62, 167],
  "sky-other": [95, 219, 255],
  stone: [230, 182, 19],
  // tree: [140, 104, 47],
  "water-other": [128, 255, 255],
  // "ground-other": [252, 177, 204],
};
const allSemanticColors = getAllSemanticColors();

export const colorString2Label: Dictionary<number> = {};
export const importantColorString2Label: Dictionary<number> = {};
export const allSemanticColorStrings: Dictionary<string> = {};
Object.entries(allSemanticColors).forEach(([key, color]) => {
  const colorString = colorToString(color);
  allSemanticColorStrings[key] = colorString;
  const label = reversedSemanticMap[key];
  colorString2Label[colorString] = label;
  if (importantColors[key]) {
    importantColorString2Label[colorString] = label;
  }
});
export const defaultSemanticType = "clouds";
export const defaultSemanticColorString = allSemanticColorStrings[defaultSemanticType];

export const defaultBrushColor = "rgba(96, 120, 244, 0.7)";

export const BATCH_SUPPORT_META_TYPE: MetaType["type"][] = [
  "txt2img",
  "img2img",
  "txt2img.variation",
  "img2img.variation",
  "txt2img.inpainting",
];

export const DEFAULT_NEGATIVE_PROMPT_EMBEDDING = "<easynegative>";
